import { Injectable } from "@angular/core";
import { PersonDetail } from "@common/ADAPT.Common.Model/person/person-detail";
import { PersonFlag, PersonFlagDefaults } from "@common/ADAPT.Common.Model/person/person-flag.enum";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { GuidedTour, GuidedTourService } from "@common/lib/guided-tour/guided-tour.service";
import { UserService } from "@common/user/user.service";
import { ResponsiveService } from "@common/ux/responsive/responsive.service";
import { firstValueFrom, lastValueFrom, switchMap } from "rxjs";
import { filter, map, startWith, tap } from "rxjs/operators";
import { PersonService } from "./person.service";

@Injectable({
    providedIn: "root",
})
export class PersonFlagService {
    public static readonly FlagEnabledValue = "true";
    public static readonly FlagDisabledValue = "false";

    public constructor(
        private personService: PersonService,
        private commonDataService: CommonDataService,
        private guidedTourService: GuidedTourService,
        private userService: UserService,
        private responsiveService: ResponsiveService,
        private rxjsBreezeService: RxjsBreezeService,
    ) { }

    public isFlagEnabled$(flag: PersonFlag) {
        return this.rxjsBreezeService.entityTypeChanged(PersonDetail).pipe(
            startWith(undefined),
            filter((personDetail) => !personDetail || personDetail.personId === this.userService.getCurrentPersonId()),
            switchMap(() => this.isFlagEnabled(flag)),
        );
    }

    public isFlagEnabled(flag: PersonFlag) {
        return this.personService.getPersonDetail(this.userService.getCurrentPersonId(), flag).pipe(
            map((detail) => this.isFlagDetailEnabled(flag, detail)),
        );
    }

    public setFlagAndSave(flag: PersonFlag, enabled: boolean) {
        return this.setFlag(flag, enabled).pipe(
            switchMap((detail) => this.commonDataService.saveEntities([detail]).pipe(
                map(() => enabled),
            )),
        );
    }

    public setFlag(flag: PersonFlag, enabled: boolean) {
        const personId = this.userService.getCurrentPersonId();
        if (!personId) {
            throw new Error("Cannot set person flag without a current person ID. This should not happen as current person will be set right after authentication.");
        }

        return this.personService.getOrCreatePersonDetail(personId, flag).pipe(
            tap((detail) => this.setFlagDetail(detail, enabled)),
        );
    }

    public async setFlagAndRunTour(flag: PersonFlag, tour?: GuidedTour) {
        const personId = this.userService.getCurrentPersonId();
        if (personId) {
            if (!await lastValueFrom(this.isFlagEnabled(flag))
                && !await firstValueFrom(this.responsiveService.isMobileSize$)) {
                this.setFlagAndSave(flag, true).subscribe();
                this.guidedTourService.run(tour, true);
            }
        }
    }

    private setFlagDetail(detail: PersonDetail, enabled: boolean) {
        detail.value = enabled
            ? PersonFlagService.FlagEnabledValue
            : PersonFlagService.FlagDisabledValue;
    }

    private isFlagDetailEnabled(flag: PersonFlag, detail?: PersonDetail) {
        // no detail set, use the default if exists
        if (!detail) {
            return PersonFlagDefaults[flag]?.() ?? false;
        }

        return detail.value === PersonFlagService.FlagEnabledValue;
    }
}
